import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';

import classNames from 'classnames';
import {runInAction} from 'mobx';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';
import {type FileApiModel, fileStore} from '@yourcoach/shared/api/media/file';
import ImageAddIcon from '@yourcoach/shared/assets/icons/img-add.svg';
import {Image} from '@yourcoach/shared/uikit/Image';
import {getFileSrc} from '@yourcoach/shared/utils/entity/file';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';
import {
  getDescriptionSchema,
  getTitleSchema,
  validateFields,
} from '@yourcoach/shared/utils/validation';
import {createHtmlInputField} from '@yourcoach/shared/utils/validation/createHtmlInputField';

import Button from '@src/components/Button';
import ImageEditor from '@src/components/ImageEditor';
import Modal from '@src/components/ModalNew';
import TextArea from '@src/components/TextArea';
import TextField from '@src/components/TextField';
import useIsVisible from '@src/hooks/useIsVisible';
import AddFileModal from '@src/modules/library/AddFileModal';

import type {TabProps, TabRef} from './useTabs';

import styles from './Description.module.css';
import commonStyles from './styles.module.css';

export const PROGRAM_AVATAR_UPLOADING_KEY = 'program_avatar';

const CRUProgramDescription = forwardRef<TabRef, TabProps>(
  ({program, userIsOwner}, ref) => {
    const {t} = useI18n();

    useImperativeHandle(ref, () => ({
      getData,
    }));

    const [avatar, setAvatar] = useState<File | FileApiModel>();

    const avatarSource = useMemo(
      () =>
        avatar
          ? (avatar as FileApiModel)._id
            ? getFileSrc(avatar as FileApiModel, 250).url
            : URL.createObjectURL(avatar as unknown as MediaSource)
          : '',
      [avatar],
    );

    useEffect(() => {
      return () => {
        if (avatarSource && avatar instanceof File) {
          URL.revokeObjectURL(avatarSource);
        }
      };
    }, [avatar, avatarSource]);

    const [addAvatarModalIsOpen, showAddAvatarModal, hideAddAvatarModal] =
      useIsVisible();

    const [editAvatarModalIsVisible, showEditAvatarModal, hideEditAvatarModal] =
      useIsVisible();

    const fields = useRef({
      description: createHtmlInputField<HTMLTextAreaElement>('description', {
        defaultValue: '',
        validationRule: getDescriptionSchema(),
      }),
      title: createHtmlInputField('title', {
        defaultValue: '',
        validationRule: getTitleSchema(),
      }),
    }).current;

    const getData = useCallback(() => {
      if (validateFields(fields)) {
        return {
          avatar: avatar || null,
          description: fields.description.value,
          editions: program && program.editions ? program.editions : [],
          title: fields.title.value,
        };
      }
    }, [avatar, fields, program]);

    useEffect(() => {
      if (program) {
        runInAction(() => {
          fields.title.value = program.title || fields.title.value;
          fields.description.value =
            program.description || fields.description.value;
        });

        if (program.avatar) {
          setAvatar(program.avatar);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAvatarClick = useCallback(() => {
      showAddAvatarModal();
    }, [showAddAvatarModal]);

    const onAvatarSelect = useCallback(
      (files: File[]) => {
        hideAddAvatarModal();

        const [file] = files;

        if (file) {
          fileStore.upload(file, {
            name: file.name,
            uploadingKey: PROGRAM_AVATAR_UPLOADING_KEY,
          });
        }

        setAvatar(files[0]);
      },
      [hideAddAvatarModal],
    );

    const onEditAvatarButtonClick = useCallback(() => {
      showEditAvatarModal();
    }, [showEditAvatarModal]);

    const onAvatarEdit = useCallback(
      (file: File) => {
        hideEditAvatarModal();
        onAvatarSelect([file]);
      },
      [hideEditAvatarModal, onAvatarSelect],
    );

    const onRemoveAvatarButtonClick = useCallback(() => {
      setAvatar(undefined);
    }, []);

    return (
      <div>
        <div className={commonStyles.dataRow}>
          {!userIsOwner ? (
            <div className={commonStyles.message}>
              {t('shared:CRUProgramDescription.edit_description_label')}
            </div>
          ) : null}
        </div>
        <div className={commonStyles.dataRow}>
          <h3 className={commonStyles.header}>
            {t('shared:CRUProgramDescription.avatar_label')}
            {':'}
          </h3>
          <AddFileModal
            fileType="image"
            isOpen={addAvatarModalIsOpen}
            mode="select"
            onAfterClose={hideAddAvatarModal}
            onSelect={onAvatarSelect}
          />
          <div className={styles.avatarContainer}>
            <div
              className={classNames(
                styles.avatar,
                !userIsOwner && styles.disabled,
              )}
              data-test-id={TestID.create_program_description.button.cover}
              onClick={userIsOwner ? onAvatarClick : undefined}
            >
              <Image
                placeholder={
                  <div className={styles.avatarPlaceholder}>
                    <ImageAddIcon />
                  </div>
                }
                src={avatarSource}
              />
            </div>
            {avatar && userIsOwner ? (
              <>
                <Button
                  className={styles.avatarActionButton}
                  onClick={onEditAvatarButtonClick}
                >
                  {t('shared:edit')}
                </Button>
                <Button
                  className={classNames(
                    styles.avatarActionButton,
                    styles.danger,
                  )}
                  onClick={onRemoveAvatarButtonClick}
                >
                  {t('shared:remove')}
                </Button>
              </>
            ) : null}
          </div>
          {avatar ? (
            <Modal
              isOpen={editAvatarModalIsVisible}
              onAfterClose={hideEditAvatarModal}
              title={t('shared:CRUProgramDescription.edit_avatar_modal_title')}
            >
              <ImageEditor
                cropVariant="square"
                image={avatar}
                onSuccess={onAvatarEdit}
              />
            </Modal>
          ) : null}
        </div>
        <div className={commonStyles.dataRow}>
          <h3 className={commonStyles.header}>
            {t('shared:CRUProgramDescription.title_label')}
            {':'}
          </h3>
          <Observer>
            {() => (
              <TextField
                data-test-id={TestID.create_program_description.input.title}
                disabled={!userIsOwner}
                error={fields.title.error || ''}
                onChange={fields.title.onChange}
                placeholder={t(
                  'shared:CRUProgramDescription.title_text_input_placeholder',
                )}
                ref={fields.title.ref}
                value={fields.title.value}
              />
            )}
          </Observer>
        </div>
        <div className={commonStyles.dataRow}>
          <h3 className={commonStyles.header}>
            {t('shared:CRUProgramDescription.description_label')}
            {':'}
          </h3>
          <Observer>
            {() => (
              <TextArea
                className={styles.descriptionTextArea}
                data-test-id={
                  TestID.create_program_description.input.description
                }
                disabled={!userIsOwner}
                error={fields.description.error || ''}
                onChange={fields.description.onChange}
                placeholder={t(
                  'shared:CRUProgramDescription.description_text_input_placeholder',
                )}
                ref={fields.description.ref}
                value={fields.description.value}
              />
            )}
          </Observer>
        </div>
      </div>
    );
  },
);

export default memo(CRUProgramDescription, isEqual);
